import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LessonDetail, LessonFilter } from '@/services/syllabusService';
import { ICommonListQuery, IPagination } from '@/common/interface';
import { DEFAULT_FIRST_PAGE } from '@/common/constants';

export interface IGetListSkillsParams extends ICommonListQuery {
    skill: string;
    difficulty?: string;
    level?: string;
    lesson_mode?: string;
}

export const LIMIT_FOR_PAGINATION = 12;

const DEFAULT_ACHIEVEMENT_LIST_QUERY: IGetListSkillsParams = {
    skill: 'all',
    difficulty: 'all',
    page: DEFAULT_FIRST_PAGE,
    page_size: LIMIT_FOR_PAGINATION,
};
const DEFAULT_ACHIEVEMENT_PAGINATION: IPagination = {
    count: 0,
    page: DEFAULT_FIRST_PAGE,
    page_size: LIMIT_FOR_PAGINATION,
    total_pages: 0,
};

interface SyllabusState {
    query: IGetListSkillsParams;
    pagination: IPagination;
    lessons: LessonDetail[];
    skillCounts: { [key: string]: { complete?: number; incomplete: number; total: number } };
    currentLesson: LessonDetail | null;
    totalLessons: number;
    currentPage: number;
    pageSize: number;
    filters: LessonFilter;
    isLoading: boolean;
    error: string | null;
}

const initialState: SyllabusState = {
    query: DEFAULT_ACHIEVEMENT_LIST_QUERY,
    pagination: DEFAULT_ACHIEVEMENT_PAGINATION,
    lessons: [],
    skillCounts: {},
    currentLesson: null,
    totalLessons: 0,
    currentPage: 1,
    pageSize: 10,
    filters: {
        skill: 'speaking',
        page: 1,
        page_size: 10,
    },
    isLoading: false,
    error: null,
};

const syllabusSlice = createSlice({
    name: 'syllabus',
    initialState,
    reducers: {
        setQuery: (state, action: PayloadAction<IGetListSkillsParams>) => {
            state.query = action.payload;
        },
        // Search/paging belong to one skill list; a new skill starts from the defaults.
        resetQuery: (state) => {
            state.query = { ...DEFAULT_ACHIEVEMENT_LIST_QUERY };
        },
        setPagination: (state, action: PayloadAction<IPagination>) => {
            state.pagination = action.payload;
        },
        // Saga request actions
        fetchAiLessonsRequest: (state: SyllabusState, action: PayloadAction<IGetListSkillsParams>) => {
            state.isLoading = true;
            state.error = null;
        },
        fetchAiLessonsSuccess: (
            state,
            action: PayloadAction<{ lessons: LessonDetail[]; skillCounts: { [key: string]: { complete?: number; incomplete: number; total: number } }; count: number; page: number; page_size: number }>,
        ) => {
            state.isLoading = false;
            state.lessons = action.payload.lessons;
            state.skillCounts = action.payload.skillCounts;
            state.pagination = {
                page: action.payload.page,
                page_size: action.payload.page_size,
                count: action.payload.count,
                total_pages: Math.ceil(action.payload.count / action.payload.page_size),
            };

            state.totalLessons = action.payload.count;
            state.currentPage = action.payload.page;
            state.pageSize = action.payload.page_size;
        },
        fetchAiLessonsFailure: (state: SyllabusState, action: PayloadAction<string>) => {
            state.isLoading = false;
            state.error = action.payload;
        },

        fetchLessonByIdRequest: (state: SyllabusState, _action: PayloadAction<number>) => {
            state.isLoading = true;
            state.error = null;
        },
        fetchLessonByIdSuccess: (state: SyllabusState, action: PayloadAction<LessonDetail>) => {
            state.isLoading = false;
            state.currentLesson = action.payload;
        },
        fetchLessonByIdFailure: (state: SyllabusState, action: PayloadAction<string>) => {
            state.isLoading = false;
            state.error = action.payload;
        },

        setFilters: (state: SyllabusState, action: PayloadAction<LessonFilter>) => {
            state.filters = { ...state.filters, ...action.payload };
        },

        setCurrentPage: (state: SyllabusState, action: PayloadAction<number>) => {
            state.currentPage = action.payload;
        },

        setLoading: (state: SyllabusState, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },

        setError: (state: SyllabusState, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },

        clearError: (state: SyllabusState) => {
            state.error = null;
        },

        resetState: (state: SyllabusState) => {
            state.query = { ...DEFAULT_ACHIEVEMENT_LIST_QUERY }
            state.lessons = []
            state.skillCounts = {}
            state.currentLesson = null
            state.totalLessons = 0
            state.currentPage = 1
            state.pageSize = 10
            state.filters = {
                skill: 'speaking',
                page: 1,
                page_size: 10,
            }
            state.pagination = DEFAULT_ACHIEVEMENT_PAGINATION
            state.isLoading = false
            state.error = null
        },
    },
});

export const {
    setPagination,
    setQuery,
    resetQuery,
    fetchAiLessonsRequest,
    fetchAiLessonsSuccess,
    fetchAiLessonsFailure,
    fetchLessonByIdRequest,
    fetchLessonByIdSuccess,
    fetchLessonByIdFailure,
    setFilters,
    setCurrentPage,
    setLoading,
    setError,
    clearError,
    resetState,
} = syllabusSlice.actions;

export default syllabusSlice.reducer;
